import {CircularProgress, Pagination, Stack, Typography} from '@mui/material';
import React, {FunctionComponent, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {loadingUser, paymentHistorySelector} from '../../../store/selectors';
import withRedirectAfterLogout from '../../../utils/withRedirectAfterLogout';
import './style.scss';
import {DataGrid, GridCellParams} from "@mui/x-data-grid";
import {getUsersPaymentsHistory} from "../../../store/actions";
import {EActionStatus, EPlatformPrices, EProPlatformPrices} from "../../../models/consts";
import {EPaymentStatuses} from "../../../constants";
import {Button, Chip, WhiteCard} from "../../../components/StyledComponents";
import {Download} from "react-feather";
import Platform from "../../../models/platforms-data";

const PAGE_SIZE = 10;

const getPlatformNameByProductId = (productId: string): string | undefined => {
  if (!productId) {
    return undefined;
  }

  return (Object.keys(EPlatformPrices) as Array<keyof typeof EPlatformPrices>).find((name) => {
    const standardId = EPlatformPrices[name];
    const proId = EProPlatformPrices[name];
    return (!!standardId && standardId === productId) || (!!proId && proId === productId);
  });
};

const getPaymentPlatformName = (row: { platform?: string | null; product_id?: string }): string | undefined => {
  if (row.platform && Platform[row.platform]) {
    return row.platform;
  }
  return getPlatformNameByProductId(String(row.product_id || ''));
};

const PaymentHistory: FunctionComponent = () => {
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const userLoading = useAppSelector(loadingUser);
  const paymentHistoryItems = useAppSelector(paymentHistorySelector);
  const [loadingData, setLoadingData] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        await dispatch(getUsersPaymentsHistory()).unwrap();
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const rows = useMemo(() => paymentHistoryItems || [], [paymentHistoryItems]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const PaginationFooter = () => {
    const listLength = rows.length;
    const itemFrom = listLength ? (PAGE_SIZE * page) - PAGE_SIZE + 1 : 0;
    const itemTo = (PAGE_SIZE * page) <= listLength ? (PAGE_SIZE * page) : listLength;
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height='64px'
        px='16px'
      >
        <Typography>{`Showing ${itemFrom} to ${itemTo} of ${listLength}`}</Typography>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, v) => setPage(v)}
        />
      </Stack>
    );
  };

  return (
    <WhiteCard>
      <div id="payment-page">
        {(userLoading || loadingData) ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <div className="payment-block">
            <div className="header-block actions-header">
              <Typography variant="h2" display="block" mb="32px">
                {translate('common.default_pages.payment_history_title')}
              </Typography>
            </div>
            <div className="payment-list-container">
              {!rows.length ? (
                <Typography>{translate('common.no_results')}</Typography>
              ) : (
                <DataGrid
                  autoHeight
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableRowSelectionOnClick
                  hideFooterPagination
                  rowHeight={60}
                  paginationModel={{
                    page: page - 1,
                    pageSize: PAGE_SIZE
                  }}
                  pageSizeOptions={[PAGE_SIZE]}
                  onPaginationModelChange={(model) => {
                    setPage(model.page + 1);
                  }}
                  rows={rows}
                  columns={[
                    {
                      field: 'product_id',
                      headerName: translate('subscriptions.subscriptions_list.app'),
                      flex: 1,
                      minWidth: 140,
                      maxWidth: 150,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      renderCell: (params: GridCellParams) => {
                        const platformName = getPaymentPlatformName(params.row);
                        if (!platformName || !Platform[platformName]) {
                          return <div>-</div>;
                        }
                        return (
                          <div className='subscriptions-logo'>
                            <img src={Platform[platformName].smallLogo} className="platformLogo" loading="lazy"/>
                            {Platform[platformName].title}
                          </div>
                        );
                      },
                    },
                    {
                      field: 'created',
                      headerName: translate('subscriptions.payment_history.time'),
                      flex: 1,
                      maxWidth: 140,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'muted-cell',
                      renderCell: (params: GridCellParams) => (
                        <div>{new Date(Number(params.value + '000')).toLocaleDateString()}</div>
                      ),
                    },
                    {
                      field: 'amount_received',
                      headerName: translate('subscriptions.payment_history.amount'),
                      flex: 1,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'price-cell',
                      renderCell: (params: GridCellParams) => (
                        <div className="price">
                          {(params.value / 100).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD"
                          })}
                          <div className='price-sub'>
                            Incl. taxes and fees
                          </div>
                        </div>
                      ),
                    },
                    {
                      field: 'currency',
                      headerName: translate('subscriptions.payment_history.currency'),
                      flex: 1,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'muted-cell',
                      renderCell: (params: GridCellParams) => (
                        <div>{String(params.value).toUpperCase()}</div>
                      ),
                    },
                    {
                      field: 'status',
                      headerName: translate('subscriptions.payment_history.status'),
                      flex: 1,
                      minWidth: 120,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'subscription-table--cell',
                      renderCell: (params: GridCellParams) => (
                        <Chip
                          className={params.value === EActionStatus.SUCCEEDED ? 'success' : 'error'}
                          label={params.value === EActionStatus.SUCCEEDED ? 'success' : params.value}
                        />
                      ),
                    },
                    {
                      field: 'last4',
                      headerName: translate('subscriptions.payment_history.card'),
                      flex: 1,
                      maxWidth: 120,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'muted-cell',
                      renderCell: (params: GridCellParams) => (
                        params.value ?
                          <div>{'**** ' + params.value}</div> :
                          '-'
                      ),
                    },
                    {
                      field: 'receipt_url',
                      headerName: translate('subscriptions.payment_history.invoice'),
                      minWidth: 180,
                      flex: 1,
                      editable: false,
                      sortable: false,
                      headerClassName: 'subscription-table--header',
                      cellClassName: 'action-cell',
                      renderCell: (params: GridCellParams) => {
                        return (
                          <div className="last-column">
                            {params.row.status === EPaymentStatuses.SUCCESS && (
                              <Button className="payment-invoice-btn" variant="contained" color="primary"
                                      onClick={() => {
                                        window.open(params.value);
                                      }}>
                                {translate('subscriptions.payment_history.invoice')}
                              </Button>
                            )}
                            {params.row.invoice_pdf && (
                              <Button className="payment-receipt-btn" variant="outlined" color="primary"
                                      onClick={() => {
                                        window.open(params.row.invoice_pdf);
                                      }}>
                                <Download className='feather-icon-in-button'/>
                                {translate('subscriptions.payment_history.download_invoice')}
                              </Button>
                            )}
                          </div>
                        );
                      },
                    },
                  ]}
                  slots={{
                    footer: PaginationFooter
                  }}
                />
              )}
            </div>
          </div>
        }
      </div>
    </WhiteCard>
  );
};

export default withRedirectAfterLogout()(PaymentHistory);
